/**
 * SWD destination A2 — the intended and the actual destination of a job, whatever the kind: a
 * disposal facility, a drop-off location, or a catalog disposal well the company authorized
 * (0210; SWD and INJ alike). Corbin, 2026-10-17: "there is an intended disposal and an actual
 * disposal."
 *
 * Pure; job detail (GET /v1/jobs/:id) supplies the nested objects. At most one of each side's
 * three is set (the API's at-most-one CHECKs).
 */

import type { JobDetail } from './use-job-detail';

type Side = Pick<
  JobDetail,
  | 'plannedDisposalFacility'
  | 'actualDisposalFacility'
  | 'plannedDropoffHaulerLocation'
  | 'actualDropoffHaulerLocation'
  | 'plannedDropoffDisposalWell'
  | 'actualDropoffDisposalWell'
>;

export type DestinationPlaceKind = 'FACILITY' | 'LOCATION' | 'WELL';

export interface DestinationPlace {
  readonly kind: DestinationPlaceKind;
  readonly id: string;
  readonly name: string;
}

/** What kind of place it is, in words. */
export const DESTINATION_KIND_LABELS: Readonly<Record<DestinationPlaceKind, string>> = {
  FACILITY: 'Disposal facility',
  LOCATION: 'Your drop-off location',
  WELL: 'Your disposal well',
};

function place(
  facility: { readonly id: string; readonly displayName: string } | null | undefined,
  location: { readonly id: string; readonly name: string } | null | undefined,
  well: { readonly id: string; readonly name: string } | null | undefined,
): DestinationPlace | null {
  if (facility != null) return { kind: 'FACILITY', id: facility.id, name: facility.displayName };
  if (location != null) return { kind: 'LOCATION', id: location.id, name: location.name };
  if (well != null) return { kind: 'WELL', id: well.id, name: well.name };
  return null;
}

/** The intended (planned) destination, or null when none was planned. */
export function intendedPlace(job: Side): DestinationPlace | null {
  return place(
    job.plannedDisposalFacility,
    job.plannedDropoffHaulerLocation,
    job.plannedDropoffDisposalWell,
  );
}

/** Where the load actually went, or null before the driver arrived at a disposal. */
export function actualPlace(job: Side): DestinationPlace | null {
  return place(
    job.actualDisposalFacility,
    job.actualDropoffHaulerLocation,
    job.actualDropoffDisposalWell,
  );
}

/** The intended destination's name, or null when none was planned. */
export function intendedDestination(job: Side): string | null {
  return intendedPlace(job)?.name ?? null;
}

/** The actual destination's name, or null before the driver arrived at a disposal. */
export function actualDestination(job: Side): string | null {
  return actualPlace(job)?.name ?? null;
}

/** The load went somewhere other than planned (both are known and they differ). */
export function wentElsewhere(job: Side): boolean {
  const a = actualPlace(job);
  const i = intendedPlace(job);
  return a !== null && i !== null && (a.kind !== i.kind || a.id !== i.id);
}
